import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import api from '../services/api';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const InventoryContext = createContext();
const INVENTORY_PREFIX = '/inventory';
const PAGE_SIZE = 8;

const OPTIONS_KEYS = [
  'developers',
  'projects',
  'configurations',
  'property_types',
  'construction_statuses',
  'categories',
  'unit_statuses',
  'area_units',
  'commission_types',
  'sales_priorities',
  'project_tag_suggestions',
  'unit_property_params',
  'facing_directions',
  'nearby_place_types',
  'specification_categories',
  'media_asset_types',
  'construction_status_dates_rules',
];

/** API returns categories (with unit_types); we expose as categories. */
const API_OPTIONS_KEY_MAP = {
  developers: 'developers',
  projects: 'projects',
  configurations: 'configurations',
  property_types: 'property_types',
  construction_statuses: 'construction_statuses',
  categories: 'categories',
  unit_statuses: 'unit_statuses',
  area_units: 'area_units',
  commission_types: 'commission_types',
  facing_directions: 'facing_directions',
  nearby_place_types: 'nearby_place_types',
  specification_categories: 'specification_categories',
  media_asset_types: 'media_asset_types',
  construction_status_dates_rules: 'construction_status_dates_rules',
};

const EMPTY_OPTIONS = Object.fromEntries(OPTIONS_KEYS.map((k) => [k, []]));

export const STATUS_COLORS = {
  Available: 'success',
  Booked: 'warning',
  Sold: 'secondary',
  Blocked: 'error',
  Hold: 'info',
};

const DEVELOPER_COLORS = ['#007050', '#7c3aed', '#1d4ed8', '#c2410c', '#059669'];

// -----------------------------------------------------------------------------
// Formatters (shared by mappers)
// -----------------------------------------------------------------------------

export function fmtPrice(p) {
  if (!p) return '—';
  if (p >= 10000000) return `₹${(p / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
  if (p >= 100000) return `₹${(p / 100000).toFixed(0)}L`;
  return `₹${p.toLocaleString('en-IN')}`;
}

function formatBudgetRange(priceMin, priceMax) {
  const min = priceMin != null ? Number(priceMin) : null;
  const max = priceMax != null ? Number(priceMax) : null;
  if (min != null && max != null) {
    if (min >= 1e7 || max >= 1e7) return `₹${(min / 1e7).toFixed(1)} Cr – ₹${(max / 1e7).toFixed(1)} Cr`;
    return `₹${(min / 1e5).toFixed(0)} L – ₹${(max / 1e5).toFixed(0)} L`;
  }
  if (min != null) return min >= 1e7 ? `₹${(min / 1e7).toFixed(1)} Cr+` : `₹${(min / 1e5).toFixed(0)} L+`;
  return '';
}

function formatAreaRange(areaMin, areaMax, unit = 'sqft') {
  const min = areaMin != null ? Number(areaMin) : null;
  const max = areaMax != null ? Number(areaMax) : null;
  if (min != null && max != null) return `${min.toLocaleString('en-IN')} – ${max.toLocaleString('en-IN')} ${unit}`;
  if (min != null) return `${min.toLocaleString('en-IN')}+ ${unit}`;
  if (max != null) return `Up to ${max.toLocaleString('en-IN')} ${unit}`;
  return '';
}

// -----------------------------------------------------------------------------
// API → UI mappers
// -----------------------------------------------------------------------------

function mapProjectFromApi(p) {
  const budget_range = formatBudgetRange(p.price_min, p.price_max);
  const addr = p.address || {};
  const location = [addr.locality, addr.city].filter(Boolean).join(', ');
  const commission =
    p.commission_rate != null ? `${p.commission_rate}%${p.commission_type === 'fixed' ? ' (fixed)' : ''}` : '';
  const unitStats = p.unit_stats || { available: 0, booked: 0, hold: 0, sold: 0, blocked: 0, total: 0 };
  const totalAreaRange = formatAreaRange(p.area_min, p.area_max, p.area_unit || 'sqft') || null;
  const totalAreaDisplay = p.total_area != null && !Number.isNaN(Number(p.total_area))
    ? `${Number(p.total_area).toLocaleString('en-IN')} ${p.area_unit || 'sqft'}`
    : totalAreaRange;
  const meta = p.metadata || {};
  const address = {
    city: addr.city || p.city || '',
    state: addr.state ?? p.state ?? '',
    country: addr.country || p.country || 'IN',
    locality: addr.locality || '',
    address_line: addr.address_line || '',
    pincode: addr.pincode || '',
    latitude: addr.latitude != null ? Number(addr.latitude) : null,
    longitude: addr.longitude != null ? Number(addr.longitude) : null,
    google_place_id: addr.google_place_id || '',
    google_maps_url: addr.google_maps_url || '',
  };
  // Model fields: launch_date, possession_date, completion_date, rera_approved (metadata.rera_number only)
  return {
    id: p.id,
    project_name: p.name,
    name: p.name,
    developer: p.developer_name || '',
    developer_id: p.developer,
    location,
    city: p.city ?? address.city,
    state: p.state ?? address.state,
    country: p.country ?? address.country,
    address,
    construction_status: p.construction_status_display || p.construction_status || '',
    construction_status_value: p.construction_status,
    construction_status_display: p.construction_status_display || p.construction_status || '',
    status: p.construction_status_display || p.construction_status,
    budget_range: budget_range || null,
    budgetRange: budget_range || null,
    totalAreaRange,
    totalAreaDisplay,
    total_area: p.total_area,
    launch_date: p.launch_date ?? meta.launch_date ?? null,
    possession: p.possession_date ?? meta.possession_date ?? meta.possession ?? null,
    possession_date: p.possession_date ?? meta.possession_date ?? null,
    completion_date: p.completion_date ?? meta.completion_date ?? null,
    rera_approved: Boolean(p.rera_approved),
    rera: meta.rera_number ?? meta.rera ?? null,
    commission,
    unit_stats: unitStats,
    total: unitStats.total,
    available: unitStats.available,
    booked: unitStats.booked,
    hold: unitStats.hold,
    sold: unitStats.sold,
    blocked: unitStats.blocked,
    tags: Array.isArray(p.tags) ? p.tags : (meta.tags || []),
    key_highlights: Array.isArray(p.key_highlights) ? p.key_highlights : [],
    description: p.description,
    price_min: p.price_min,
    price_max: p.price_max,
    area_min: p.area_min,
    area_max: p.area_max,
    area_unit: p.area_unit,
    commission_rate: p.commission_rate,
    commission_type: p.commission_type,
    metadata: p.metadata,
    pricing: p.pricing && typeof p.pricing === 'object' ? p.pricing : {},
    created_at: p.created_at,
    updated_at: p.updated_at,
  };
}

function mapConfigurationFromApi(c) {
  const priceMin = c.price_min != null ? Number(c.price_min) : null;
  const priceMax = c.price_max != null ? Number(c.price_max) : null;
  const priceRange =
    priceMin != null && priceMax != null
      ? (() => {
          const inCr = priceMax >= 1e7;
          if (inCr) {
            const minCr = priceMin / 1e7;
            const maxCr = priceMax / 1e7;
            const fmt = (v) => (v % 1 === 0 ? String(v) : v.toFixed(2).replace(/\.?0+$/, ''));
            return `₹${fmt(minCr)} – ₹${fmt(maxCr)} Cr`;
          }
          const minL = priceMin / 1e5;
          const maxL = priceMax / 1e5;
          const fmtL = (v) => (v % 1 === 0 ? String(v) : v.toFixed(1).replace(/\.?0+$/, ''));
          return `₹${fmtL(minL)} – ₹${fmtL(maxL)} L`;
        })()
      : '';
  const areaMin = c.area_min != null ? Number(c.area_min) : null;
  const areaMax = c.area_max != null ? Number(c.area_max) : null;
  const areaUnit = c.area_unit || null;
  const areaRange = areaUnit ? formatAreaRange(areaMin, areaMax, areaUnit) : '';
  const meta = c.metadata || {};
  const availableCount = c.available_units ?? c.availalble_units ?? null;
  const tagsFromApi = Array.isArray(c.tags) ? c.tags : (Array.isArray(meta.tags) ? meta.tags : []);
  const matrix = c.units_matrix || {};
  return {
    id: c.id,
    name: c.name,
    category: c.category_display || c.category,
    construction_status: c.construction_status ?? null,
    construction_status_display: c.construction_status_display ?? c.construction_status ?? '',
    priceRange,
    price_min: c.price_min,
    price_max: c.price_max,
    area_min: areaMin,
    area_max: areaMax,
    area_unit: areaUnit,
    areaRange: areaRange || null,
    project: c.project,
    project_name: c.project_name,
    property_type: c.property_type,
    property_type_name: c.property_type_name,
    description: c.description,
    metadata: c.metadata,
    commission: c.commission_rate != null ? Number(c.commission_rate) : (meta.commission_rate != null ? Number(meta.commission_rate) : null),
    tags: tagsFromApi,
    key_highlights: Array.isArray(c.key_highlights) ? c.key_highlights : [],
    created_at: c.created_at,
    updated_at: c.updated_at,
    available_units: availableCount != null ? Number(availableCount) : (matrix.available != null ? Number(matrix.available) : (c.available != null ? Number(c.available) : 0)),
    total: c.total != null ? Number(c.total) : (matrix.total != null ? Number(matrix.total) : 0),
    available: c.available != null ? Number(c.available) : (matrix.available != null ? Number(matrix.available) : (availableCount != null ? Number(availableCount) : 0)),
    booked: c.booked != null ? Number(c.booked) : (matrix.booked != null ? Number(matrix.booked) : 0),
    hold: c.hold != null ? Number(c.hold) : (matrix.hold != null ? Number(matrix.hold) : 0),
    sold: c.sold != null ? Number(c.sold) : (matrix.sold != null ? Number(matrix.sold) : 0),
    config_ui_schema: c.config_ui_schema ?? null,
    config_pricing_schema: Array.isArray(c.config_pricing_schema) ? c.config_pricing_schema : [],
    shared_pricing_schema: Array.isArray(c.shared_pricing_schema) ? c.shared_pricing_schema : [],
    spec_schema: Array.isArray(c.spec_schema) ? c.spec_schema : [],
    pricing: Array.isArray(c.pricing) ? c.pricing : [],
  };
}

function mapUnitFromApi(u) {
  return {
    id: u.id,
    unitId: u.unit_code || u.name,
    project: u.project_name,
    projectId: String(u.project),
    configuration: u.configuration_name,
    configurationId: String(u.configuration),
    category: u.metadata?.category || 'residential',
    floor: u.floor ?? u.metadata?.floor ?? null,
    tower: u.unit_block ?? u.metadata?.tower ?? null,
    unit_block: u.unit_block ?? null,
    facing: u.facing ?? u.metadata?.facing ?? null,
    area: u.area != null ? Number(u.area) : null,
    areaUnit: u.area_unit || 'sqft',
    price: u.price != null ? Number(u.price) : null,
    status: (u.inventory_status_display || u.inventory_status || '').toLowerCase(),
    channelPartner: u.metadata?.channel_partner ?? null,
  };
}

function mapUnitDetailFromApi(u) {
  if (!u) return null;
  const meta = u.metadata || {};
  return {
    id: u.id,
    name: u.name || u.unit_code || '',
    unit_code: u.unit_code || null,
    project: u.project,
    project_name: u.project_name,
    configuration: u.configuration,
    configuration_name: u.configuration_name,
    inventory_status: u.inventory_status || 'available',
    inventory_status_display: u.inventory_status_display || u.inventory_status || 'Available',
    price: u.price != null ? Number(u.price) : null,
    area: Array.isArray(u.area) ? u.area : (u.area != null ? Number(u.area) : (u.total_area != null ? Number(u.total_area) : null)),
    total_area: u.total_area != null ? Number(u.total_area) : (Array.isArray(u.area) ? null : (u.area != null ? Number(u.area) : null)),
    area_unit: u.area_unit || 'sqft',
    commission_rate: u.commission_rate != null ? Number(u.commission_rate) : null,
    commission_type: u.commission_type || null,
    metadata: u.metadata || {},
    floor: u.floor ?? meta.floor ?? null,
    facing: u.facing ?? meta.facing ?? null,
    unit_block: u.unit_block ?? meta.tower_wing ?? meta.tower ?? null,
    tower_wing: u.unit_block ?? meta.tower_wing ?? meta.tower ?? null,
    tags: Array.isArray(u.tags) ? u.tags : (Array.isArray(meta.tags) ? meta.tags : []),
    key_highlights: Array.isArray(u.key_highlights) ? u.key_highlights : [],
    area_list: Array.isArray(u.area_list) ? u.area_list : [],
    amenities: meta.amenities || [],
    facilities: meta.facilities || [],
    features: meta.features || [],
    unit_schema: u.unit_schema && typeof u.unit_schema === 'object' ? u.unit_schema : null,
    created_at: u.created_at,
    updated_at: u.updated_at,
  };
}

function mapDeveloperFromApi(d) {
  const name = d.name || '';
  return {
    id: d.id,
    name,
    initials: name.slice(0, 2).toUpperCase() || '?',
    description: d.description || '',
    location: d.description || '',
    website: d.website,
    logo: d.logo,
    thumbnail: d.thumbnail,
    tags: Array.isArray(d.tags) ? d.tags : [],
    key_highlights: Array.isArray(d.key_highlights) ? d.key_highlights : [],
    color: DEVELOPER_COLORS[d.id % DEVELOPER_COLORS.length],
    projects: 0,
    units: 0,
    created_at: d.created_at,
    updated_at: d.updated_at,
  };
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export const useInventory = () => {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error('useInventory must be used within InventoryProvider');
  return ctx;
};

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

function normalizeOptionsResponse(data) {
  const d = data || {};
  const result = { ...EMPTY_OPTIONS };
  Object.entries(API_OPTIONS_KEY_MAP).forEach(([apiKey, stateKey]) => {
    try {
      const val = d[apiKey];
      if (Array.isArray(val)) result[stateKey] = val;
    } catch (err) {
      console.error('Inventory normalizeOptionsResponse:', err);
    }
  });
  if (d.construction_status_dates_rules && typeof d.construction_status_dates_rules === 'object') {
    result.construction_status_dates_rules = d.construction_status_dates_rules;
  }
  if ((!result.categories || result.categories.length === 0) && Array.isArray(d.property_categories)) {
    result.categories = d.property_categories;
  }
  return result;
}

export const InventoryProvider = ({ children }) => {
  const [developers, setDevelopers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [developersLoading, setDevelopersLoading] = useState(true);
  const [inventoryOptions, setInventoryOptions] = useState(EMPTY_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // —— Options ——
  const refetchOptions = useCallback(() => {
    setOptionsLoading(true);
    return api
      .get(`${INVENTORY_PREFIX}/options/`)
      .then((res) => setInventoryOptions(normalizeOptionsResponse(res.data)))
      .catch(() => setInventoryOptions(EMPTY_OPTIONS))
      .finally(() => setOptionsLoading(false));
  }, []);

  const fetchOptionsWithUiSchema = useCallback(() => {
    return api.get(`${INVENTORY_PREFIX}/options/`, { params: { include_unit_schema: 1 } }).then((res) => res.data || {});
  }, []);

  const fetchUnitUiSchema = useCallback(({ property_type_id, category_id } = {}) => {
    if (property_type_id != null) {
      return api
        .get(`${INVENTORY_PREFIX}/units/ui-schema/property_type_id=${property_type_id}/`)
        .then((res) => ({ unit_schema: res.data?.unit_schema ?? null, property_type_id: res.data?.property_type_id }));
    }
    // Backend has no units/ui-schema by category_id; resolve to default to avoid 404
    if (category_id != null) return Promise.resolve({ unit_schema: null });
    return Promise.resolve({ unit_schema: null });
  }, []);

  /** Configuration form schema (Add/Edit Configuration). Pass null when no property type selected for default schema. */
  const fetchConfigurationUiSchema = useCallback((propertyTypeId = null) => {
    const url =
      propertyTypeId != null
        ? `${INVENTORY_PREFIX}/configuration/ui-schema/property_type_id=${propertyTypeId}/`
        : `${INVENTORY_PREFIX}/configuration/ui-schema/`;
    return api.get(url).then((res) => ({
      property_type_id: res.data?.property_type_id ?? null,
      property_type_name: res.data?.property_type_name ?? null,
      property_type_value: res.data?.property_type_value ?? null,
      config_ui_schema: res.data?.config_ui_schema ?? [],
      config_pricing_schema: res.data?.config_pricing_schema ?? [],
      shared_pricing_schema: res.data?.shared_pricing_schema ?? [],
      spec_schema: res.data?.spec_schema ?? [],
    }));
  }, []);

  // —— Projects ——
  const refetchProjects = useCallback((developerId = null) => {
    setProjectsLoading(true);
    const params = developerId != null && developerId !== '' ? { developer: developerId } : {};
    return api
      .get(`${INVENTORY_PREFIX}/projects/`, { params })
      .then((res) => {
        const data = res.data;
        setProjects(Array.isArray(data) ? data.map(mapProjectFromApi) : []);
      })
      .catch(() => setProjects([]))
      .finally(() => setProjectsLoading(false));
  }, []);

  const refetchDevelopers = useCallback(() => {
    setDevelopersLoading(true);
    return api
      .get(`${INVENTORY_PREFIX}/developers/`)
      .then((res) => {
        const data = res.data;
        setDevelopers(Array.isArray(data) ? data.map(mapDeveloperFromApi) : []);
      })
      .catch(() => setDevelopers([]))
      .finally(() => setDevelopersLoading(false));
  }, []);

  React.useEffect(() => {
    refetchProjects();
  }, [refetchProjects]);
  React.useEffect(() => {
    refetchDevelopers();
  }, [refetchDevelopers]);
  React.useEffect(() => {
    refetchOptions();
  }, [refetchOptions]);

  const fetchProjectsPage = useCallback(async (page = 1, pageSize = PAGE_SIZE, developerId = null) => {
    try {
      const params = developerId != null && developerId !== '' ? { developer: developerId } : {};
      const res = await api.get(`${INVENTORY_PREFIX}/projects/`, { params });
      const data = res.data;
      if (Array.isArray(data)) {
        const mapped = data.map(mapProjectFromApi);
        const start = (page - 1) * pageSize;
        const results = mapped.slice(start, start + pageSize);
        return { results, hasMore: start + results.length < mapped.length };
      }
    } catch (err) {
      console.error('Inventory fetchProjectsPage:', err);
    }
    return { results: [], hasMore: false };
  }, []);

  const getProjectDetail = useCallback(async (projectId) => {
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/projects/${projectId}/`);
      return mapProjectFromApi(res.data);
    } catch (err) {
      console.error('Inventory getProjectDetail:', err);
      return null;
    }
  }, []);

  const getProjectInventorySummary = useCallback(async (projectId) => {
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/projects/${projectId}/inventory-summary/`);
      const d = res.data || {};
      return {
        total: d.total ?? 0,
        available: d.available ?? 0,
        booked: d.booked ?? 0,
        sold: d.sold ?? 0,
        blocked: d.blocked ?? 0,
        not_available: d.not_available ?? 0,
      };
    } catch (err) {
      console.error('Inventory getProjectInventorySummary:', err);
      return { total: 0, available: 0, booked: 0, sold: 0, blocked: 0, not_available: 0 };
    }
  }, []);

  const createProject = useCallback(
    async (payload) => {
      const res = await api.post(`${INVENTORY_PREFIX}/projects/`, payload);
      const created = mapProjectFromApi(res.data);
      setProjects((prev) => [...prev, created]);
      refetchOptions();
      return created;
    },
    [refetchOptions]
  );

  const updateProject = useCallback(async (id, payload) => {
    const res = await api.patch(`${INVENTORY_PREFIX}/projects/${id}/`, payload);
    const updated = mapProjectFromApi(res.data);
    setProjects((prev) => prev.map((p) => (p.id === id ? updated : p)));
    return updated;
  }, []);

  const deleteProject = useCallback(async (id) => {
    await api.delete(`${INVENTORY_PREFIX}/projects/${id}/`);
    setProjects((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // —— Configurations ——
  const getConfigsByProjectId = useCallback(async (projectId) => {
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/projects/${projectId}/configurations/`);
      const data = res.data;
      return Array.isArray(data) ? data.map(mapConfigurationFromApi) : [];
    } catch (err) {
      console.error('Inventory getConfigsByProjectId:', err);
      return [];
    }
  }, []);

  const getProjectMedia = useCallback(async (projectId) => {
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/media/`, {
        params: { upload_for: 'project', reference_id: projectId },
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      console.error('Inventory getProjectMedia:', err);
      return [];
    }
  }, []);

  const getConfigurationMedia = useCallback(async (configurationId) => {
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/media/`, {
        params: { upload_for: 'configuration', reference_id: configurationId },
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      console.error('Inventory getConfigurationMedia:', err);
      return [];
    }
  }, []);

  const getMedia = useCallback(async (upload_for, reference_id) => {
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/media/`, {
        params: { upload_for, reference_id },
      });
      if (res.data && Array.isArray(res.data.groups)) return res.data;
      if (Array.isArray(res.data)) {
        return { groups: [{ group_name: 'Other', assets: res.data }] };
      }
      return { groups: [] };
    } catch (err) {
      console.error('Inventory getMedia:', err);
      return { groups: [] };
    }
  }, []);

  const uploadMedia = useCallback(async (formData) => {
    // FormData must be sent as multipart/form-data; do not send Content-Type so axios sets it with boundary
    const config = formData instanceof FormData
      ? { headers: { 'Content-Type': undefined } }
      : {};
    const res = await api.post(`${INVENTORY_PREFIX}/media/`, formData, config);
    return res.data;
  }, []);

  const addMediaLinks = useCallback(async (asset_model, reference_id, payload) => {
    const res = await api.post(`${INVENTORY_PREFIX}/media/`, {
      asset_model,
      reference_id,
      ...payload,
    });
    return res.data;
  }, []);

  const deleteMedia = useCallback(async (assetId) => {
    await api.delete(`${INVENTORY_PREFIX}/media/${assetId}/`);
  }, []);

  const deleteMediaBulk = useCallback(async (assetIds) => {
    const ids = Array.isArray(assetIds) ? assetIds.filter((id) => id != null) : [];
    if (ids.length === 0) return;
    await api.post(`${INVENTORY_PREFIX}/media/bulk-delete/`, { ids });
  }, []);

  const getConfig = useCallback(async (projectId, configId) => {
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/projects/${projectId}/configurations/${configId}/`);
      return mapConfigurationFromApi(res.data);
    } catch (err) {
      console.error('Inventory getConfig:', err);
      return null;
    }
  }, []);

  const fetchConfiguration = useCallback(async (projectId, configId) => {
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/projects/${projectId}/configurations/${configId}/`);
      return mapConfigurationFromApi(res.data);
    } catch (err) {
      console.error('Inventory fetchConfiguration:', err);
      return null;
    }
  }, []);

  const createConfiguration = useCallback(
    async (projectId, payload) => {
      const res = await api.post(`${INVENTORY_PREFIX}/projects/${projectId}/configurations/`, payload);
      refetchOptions();
      return mapConfigurationFromApi(res.data);
    },
    [refetchOptions]
  );

  const updateConfiguration = useCallback(async (projectId, id, payload) => {
    const res = await api.patch(`${INVENTORY_PREFIX}/projects/${projectId}/configurations/${id}/`, payload);
    return mapConfigurationFromApi(res.data);
  }, []);

  const deleteConfiguration = useCallback(async (projectId, id) => {
    await api.delete(`${INVENTORY_PREFIX}/projects/${projectId}/configurations/${id}/`);
  }, []);

  // —— Units ——
  const fetchSearchUnits = useCallback(async () => {
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/units/`);
      const data = res.data;
      return Array.isArray(data) ? data.map(mapUnitFromApi) : [];
    } catch (err) {
      console.error('Inventory fetchSearchUnits:', err);
      return [];
    }
  }, []);

  const fetchUnitsByConfiguration = useCallback(async (projectId, configurationId) => {
    try {
      const res = await api.get(
        `${INVENTORY_PREFIX}/projects/${projectId}/configurations/${configurationId}/units/`
      );
      const data = res.data;
      if (Array.isArray(data)) {
        return data.map((u) => ({
          id: u.id,
          name: u.name ?? u.unit_code,
          unit_code: u.unit_code ?? null,
          area: u.area,
          area_list: Array.isArray(u.area_list) ? u.area_list : [],
          area_unit: u.area_unit ?? 'sqft',
          price: u.price,
          inventory_status: u.inventory_status ?? u.inventory_status_display,
          commission_rate: u.commission_rate,
          commission_type: u.commission_type,
          key_highlights: Array.isArray(u.key_highlights) ? u.key_highlights : [],
          tags: Array.isArray(u.tags) ? u.tags : [],
          metadata: u.metadata || {},
          _raw: u,
        }));
      }
    } catch (err) {
      console.error('Inventory fetchUnitsByConfiguration:', err);
    }
    return [];
  }, []);

  const fetchUnit = useCallback(async (projectId, configurationId, unitId) => {
    try {
      const res = await api.get(
        `${INVENTORY_PREFIX}/projects/${projectId}/configurations/${configurationId}/units/${unitId}/`
      );
      return mapUnitDetailFromApi(res.data);
    } catch (err) {
      console.error('Inventory fetchUnit:', err);
      return null;
    }
  }, []);

  const createUnit = useCallback(async (projectId, configurationId, payload) => {
    const res = await api.post(
      `${INVENTORY_PREFIX}/projects/${projectId}/configurations/${configurationId}/units/`,
      payload
    );
    return res.data;
  }, []);

  const updateUnit = useCallback(async (projectId, configurationId, id, payload) => {
    const res = await api.patch(
      `${INVENTORY_PREFIX}/projects/${projectId}/configurations/${configurationId}/units/${id}/`,
      payload
    );
    return mapUnitDetailFromApi(res.data) || res.data;
  }, []);

  const deleteUnit = useCallback(async (projectId, configurationId, id) => {
    await api.delete(
      `${INVENTORY_PREFIX}/projects/${projectId}/configurations/${configurationId}/units/${id}/`
    );
  }, []);

  // —— Developers & options ——
  const fetchDeveloperOptions = useCallback(async () => {
    if (inventoryOptions.developers?.length > 0) return inventoryOptions.developers;
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/developers/options/`);
      return res.data || [];
    } catch (err) {
      console.error('Inventory fetchDeveloperOptions:', err);
      return [];
    }
  }, [inventoryOptions.developers]);

  const fetchPropertyTypeOptions = useCallback(async () => {
    if (inventoryOptions.property_types?.length > 0) return inventoryOptions.property_types;
    try {
      const res = await api.get(`${INVENTORY_PREFIX}/property-types/options/`);
      return res.data || [];
    } catch (err) {
      console.error('Inventory fetchPropertyTypeOptions:', err);
      return [];
    }
  }, [inventoryOptions.property_types]);

  const createDeveloper = useCallback(
    async (payload) => {
      const isFormData =
        typeof FormData !== 'undefined' && payload instanceof FormData;
      const res = await api.post(
        `${INVENTORY_PREFIX}/developers/`,
        payload,
        isFormData
          ? {
              headers: { 'Content-Type': 'multipart/form-data' },
            }
          : undefined
      );
      const created = mapDeveloperFromApi(res.data);
      setDevelopers((prev) => [...prev, created]);
      refetchOptions();
      return created;
    },
    [refetchOptions]
  );

  const updateDeveloper = useCallback(async (id, payload) => {
    const isFormData =
      typeof FormData !== 'undefined' && payload instanceof FormData;
    const res = await api.patch(
      `${INVENTORY_PREFIX}/developers/${id}/`,
      payload,
      isFormData
        ? {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        : undefined
    );
    const updated = mapDeveloperFromApi(res.data);
    setDevelopers((prev) => prev.map((d) => (d.id === id ? updated : d)));
    return updated;
  }, []);

  const deleteDeveloper = useCallback(async (id) => {
    await api.delete(`${INVENTORY_PREFIX}/developers/${id}/`);
    setDevelopers((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const value = useMemo(
    () => ({
      developers,
      setDevelopers,
      developersLoading,
      projects,
      setProjects,
      projectsLoading,
      inventoryOptions,
      optionsLoading,
      refetchOptions,
      fetchOptionsWithUiSchema,
      fetchUnitUiSchema,
      fetchConfigurationUiSchema,
      refetchProjects,
      refetchDevelopers,
      fetchProjectsPage,
      fetchSearchUnits,
      fetchUnitsByConfiguration,
      PAGE_SIZE,
      units,
      setUnits,
      selectedProjectId,
      setSelectedProjectId,
      getConfigsByProjectId,
      getConfig,
      getProjectDetail,
      getProjectMedia,
      getConfigurationMedia,
      getMedia,
      uploadMedia,
      addMediaLinks,
      deleteMedia,
      deleteMediaBulk,
      getProjectInventorySummary,
      fetchConfiguration,
      statusColors: STATUS_COLORS,
      createProject,
      updateProject,
      deleteProject,
      createConfiguration,
      updateConfiguration,
      deleteConfiguration,
      createUnit,
      updateUnit,
      deleteUnit,
      fetchUnit,
      fetchDeveloperOptions,
      fetchPropertyTypeOptions,
      createDeveloper,
      updateDeveloper,
      deleteDeveloper,
    }),
    [
      developers,
      developersLoading,
      projects,
      projectsLoading,
      inventoryOptions,
      optionsLoading,
      units,
      selectedProjectId,
      refetchOptions,
      fetchOptionsWithUiSchema,
      fetchUnitUiSchema,
      fetchConfigurationUiSchema,
      refetchProjects,
      refetchDevelopers,
      fetchProjectsPage,
      fetchSearchUnits,
      fetchUnitsByConfiguration,
      getConfigsByProjectId,
      getConfig,
      getProjectDetail,
      getProjectMedia,
      getConfigurationMedia,
      getMedia,
      uploadMedia,
      addMediaLinks,
      deleteMedia,
      deleteMediaBulk,
      getProjectInventorySummary,
      fetchConfiguration,
      createProject,
      updateProject,
      deleteProject,
      createConfiguration,
      updateConfiguration,
      deleteConfiguration,
      createUnit,
      updateUnit,
      deleteUnit,
      fetchUnit,
      fetchDeveloperOptions,
      fetchPropertyTypeOptions,
      createDeveloper,
      updateDeveloper,
      deleteDeveloper,
    ]
  );

  return <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>;
};
